import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import { parseAnnotations, parseAnswer, reviewUnitInfo } from '../components/AttemptReview';
import { A4_LOGICAL_WIDTH } from '../utils/answers';
import { croppedLogicalHeight, renderPageToPngDataUrl } from '../utils/inkRender';
import type {
  AnnotationMap, DescriptiveAnswer, McqAnswer, ReviewBundle, StrokesBlock, TextBlock,
} from '../types/models';

// Builds a downloadable "answer sheet" PDF for one exam attempt: ONLY the student's answers
// (handwriting rendered as page images, typed text, and the MCQ options they picked), preceded
// by a small question-number label. Question prompts, marks and grading are deliberately left
// out. A single-attempt download saves one PDF; a multi download zips one PDF per student.

// A4 portrait in millimetres (jsPDF `unit: 'mm'`).
const MARGIN = 12;
const TEXT = [17, 24, 39] as const;      // near-black body text
const MUTED = [107, 114, 128] as const;  // muted grey for meta/captions
const ACCENT = [37, 99, 235] as const;   // blue for the evaluator's remark

/** The date the student attended the exam — submission time, falling back to start time. */
function attemptDate(b: ReviewBundle): Date | null {
  const raw = b.submittedAt ?? b.startedAt;
  return raw ? new Date(raw) : null;
}

/** YYYY-MM-DD for filenames (locale-independent, filesystem-safe). */
function dateForFile(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Collapse anything filesystem-unfriendly to single underscores. */
function sanitize(s: string): string {
  return (s || '').trim().replace(/[^\w-]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
}

/** `RegNo_ExamName_dateOfExamAttended.pdf` — the filename the user asked for. */
export function answerSheetFileName(b: ReviewBundle, ext = 'pdf'): string {
  const reg = sanitize(b.rollNumber || 'NoRegNo');
  const exam = sanitize(b.examTitle || 'Exam');
  const d = attemptDate(b);
  return `${reg}_${exam}_${d ? dateForFile(d) : 'NA'}.${ext}`;
}

/** Zip name for a whole-exam (multi) download. */
export function answerSheetsZipName(examTitle: string): string {
  return `${sanitize(examTitle || 'Exam')}_answer_sheets.zip`;
}

const letter = (i: number) => String.fromCharCode(97 + i); // a, b, c…

/** Build the answer-sheet PDF document for one attempt. */
export function buildAnswerSheetDoc(bundle: ReviewBundle): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const CW = PW - MARGIN * 2;
  let y = MARGIN;

  const newPage = () => { doc.addPage(); y = MARGIN; };
  const ensure = (h: number) => { if (y + h > PH - MARGIN) newPage(); };

  /** Write wrapped text in the flowing region; advances the `y` cursor. */
  function write(text: string, o: {
    size?: number; bold?: boolean; color?: readonly number[]; gap?: number; indent?: number;
  } = {}) {
    const size = o.size ?? 11;
    const lh = size * 0.3528 * 1.32;           // pt → mm, with line spacing
    const indent = o.indent ?? 0;
    const c = o.color ?? TEXT;
    doc.setFont('helvetica', o.bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(c[0], c[1], c[2]);
    for (const line of doc.splitTextToSize(text, CW - indent)) {
      ensure(lh);
      doc.text(line, MARGIN + indent, y + lh * 0.75);
      y += lh;
    }
    if (o.gap) y += o.gap;
  }

  /** Place one rasterised ink page in the flowing layout, cropped to its content, with a small
   *  caption above it. Short answers pack together instead of each taking a whole page; staff
   *  red-pen corrections are drawn on top of the student's ink. Blank pages are skipped. */
  function placeInkImage(block: StrokesBlock, caption: string, annotation?: AnnotationMap) {
    const anno = annotation?.[block.id];
    const img = renderPageToPngDataUrl(block, 2, anno);
    if (!img) return;
    const bw = block.width || A4_LOGICAL_WIDTH;
    const cropH = croppedLogicalHeight(block, anno);
    if (cropH <= 0) return;
    const capH = 5;
    let w = CW;
    let h = w * (cropH / bw);
    // Never let a single tall image exceed a page; scale it down and centre if so.
    const maxImgH = PH - MARGIN * 2 - capH - 3;
    if (h > maxImgH) { h = maxImgH; w = h * (bw / cropH); }
    ensure(capH + h + 4);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text(caption, MARGIN, y + 3);
    y += capH;
    doc.addImage(img, 'PNG', MARGIN + (CW - w) / 2, y, w, h);
    y += h + 4;
  }

  /** Render one descriptive body (a whole question or one of its parts). */
  function descriptiveBody(label: string, pages: StrokesBlock[], blocks: TextBlock[], annotation?: AnnotationMap) {
    const inkPages = pages.filter(p => p.strokes.length > 0);
    const typed = blocks.filter(b => b.value.trim().length > 0);
    if (inkPages.length === 0 && typed.length === 0) {
      write(label, { size: 11, bold: true });
      write('No answer submitted.', { size: 10, color: MUTED, indent: 4, gap: 3 });
      return;
    }
    inkPages.forEach((p, i) =>
      placeInkImage(p, `${label} — page ${i + 1} of ${inkPages.length}`, annotation));
    if (typed.length > 0) {
      ensure(12);
      write(`${label} — typed answer`, { size: 11, bold: true, gap: 0.5 });
      typed.forEach(b => write(b.value, { size: 10.5, indent: 3, gap: 2 }));
    }
  }

  // ---- Header (once, top of page 1) --------------------------------------------------------
  const reg = bundle.rollNumber ? `Reg No: ${bundle.rollNumber}` : 'Reg No: —';
  const d = attemptDate(bundle);
  write('ANSWER SHEET', { size: 9, bold: true, color: MUTED, gap: 0.5 });
  write(`${bundle.studentName}  ·  ${reg}`, { size: 14, bold: true, gap: 0.5 });
  write([bundle.subjectName, bundle.examTitle].filter(Boolean).join(' — ') || bundle.examTitle,
    { size: 10.5, color: MUTED });
  write([
    bundle.batchName,
    d ? `Attended: ${d.toLocaleDateString()}` : null,
    `Status: ${bundle.status}`,
  ].filter(Boolean).join('   ·   '), { size: 9.5, color: MUTED, gap: 2.5 });
  doc.setDrawColor(210);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PW - MARGIN, y);
  y += 5;

  // ---- Answers -----------------------------------------------------------------------------
  const unitInfo = reviewUnitInfo(bundle.questions);
  for (const q of bundle.questions) {
    const info = unitInfo.get(q.id);
    const base = `Q${info?.unit ?? '?'}${info?.alt ? ` (Option ${info.alt})` : ''}`;
    const answer = parseAnswer(q.answerJson);
    const hasParts = (q.parts?.length ?? 0) > 0;
    const annotation = parseAnnotations(q.annotationJson);

    if (q.type === 'Descriptive' || hasParts) {
      const d2 = answer && answer.kind === 'descriptive' ? (answer as DescriptiveAnswer) : null;
      if (hasParts) {
        const byId = new Map((d2?.parts ?? []).map(p => [p.partId, p]));
        (q.parts ?? []).forEach((part, i) => {
          const slot = byId.get(part.id);
          descriptiveBody(`${base} (${letter(i)})`, slot?.pages ?? [], slot?.blocks ?? [], annotation);
        });
      } else {
        descriptiveBody(base, d2?.pages ?? [], d2?.blocks ?? [], annotation);
      }
    } else {
      // MCQ: list the option(s) the student selected.
      write(base, { size: 11, bold: true });
      const picked = answer && answer.kind === 'mcq'
        ? new Set((answer as McqAnswer).selectedOptionIds) : new Set<string>();
      const opts = [...q.options].sort((a, b) => a.orderIndex - b.orderIndex);
      const chosen = opts.filter(o => picked.has(o.id));
      if (chosen.length === 0) {
        write('No answer submitted.', { size: 10, color: MUTED, indent: 4 });
      } else {
        chosen.forEach(o => {
          const idx = opts.indexOf(o);
          write(`${String.fromCharCode(65 + idx)})  ${o.text}`, { size: 10.5, indent: 4 });
        });
      }
      y += 3;
    }

    // The evaluator's per-question remark, shown to the student on the sheet.
    if (q.graderRemark && q.graderRemark.trim()) {
      write(`Remark — ${base}: ${q.graderRemark.trim()}`,
        { size: 10, color: ACCENT, indent: 2, gap: 3 });
    }
  }

  return doc;
}

/** Trigger a browser download for a Blob. */
function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download a single attempt's answer sheet as `RegNo_ExamName_date.pdf`. */
export function downloadAnswerSheet(bundle: ReviewBundle) {
  buildAnswerSheetDoc(bundle).save(answerSheetFileName(bundle));
}

/**
 * Download many attempts' answer sheets as a single ZIP, one `RegNo_ExamName_date.pdf` inside per
 * student. Yields to the event loop between attempts so the UI (and the caller's progress
 * callback) stays responsive for a whole class.
 */
export async function downloadAnswerSheetsZip(
  bundles: ReviewBundle[],
  zipName: string,
  onProgress?: (done: number, total: number) => void,
) {
  const zip = new JSZip();
  const used = new Set<string>();
  for (let i = 0; i < bundles.length; i++) {
    const b = bundles[i];
    let name = answerSheetFileName(b);
    if (used.has(name.toLowerCase())) {
      const baseName = name.replace(/\.pdf$/i, '');
      let k = 2;
      while (used.has(`${baseName}_${k}.pdf`.toLowerCase())) k++;
      name = `${baseName}_${k}.pdf`;
    }
    used.add(name.toLowerCase());
    zip.file(name, buildAnswerSheetDoc(b).output('blob'));
    onProgress?.(i + 1, bundles.length);
    await new Promise(r => setTimeout(r, 0));
  }
  triggerBlobDownload(await zip.generateAsync({ type: 'blob' }), zipName);
}
